import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../stores/app';
import { listCampaigns, syncCampaign, activateCampaign, pauseCampaign } from '../api/meta';
import { EmptyState } from '../components/EmptyState';

export function Campaigns() {
  const sessionId = useAppStore((s) => s.activeSessionId);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', sessionId],
    queryFn: () => listCampaigns(sessionId!),
    enabled: !!sessionId,
  });

  const syncMutation = useMutation({
    mutationFn: syncCampaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const activateMutation = useMutation({
    mutationFn: activateCampaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const pauseMutation = useMutation({
    mutationFn: pauseCampaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  if (!sessionId) {
    return (
      <EmptyState
        icon="👆"
        title="Keine Session ausgewählt"
        description="Wähle oben eine Session aus."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const campaigns = data ?? [];

  if (campaigns.length === 0) {
    return (
      <EmptyState
        icon="📢"
        title="Keine Kampagnen"
        description="Erstelle Kampagnen über die Problem- oder Lösungsdetailseite."
      />
    );
  }

  // Summary
  const totalSpend = campaigns.reduce((s, c) => s + (c.spend ?? 0), 0);
  const totalClicks = campaigns.reduce((s, c) => s + (c.clicks ?? 0), 0);
  const totalImpressions = campaigns.reduce((s, c) => s + (c.impressions ?? 0), 0);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Kampagnen</h1>
        <span className="text-sm text-gray-500">{campaigns.length} Kampagnen</span>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-500">Impressions</p>
          <p className="text-lg font-bold">{totalImpressions.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-500">Clicks</p>
          <p className="text-lg font-bold">{totalClicks.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-500">CTR</p>
          <p className="text-lg font-bold">
            {totalImpressions > 0
              ? `${((totalClicks / totalImpressions) * 100).toFixed(2)}%`
              : '–'}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-500">Ausgaben</p>
          <p className="text-lg font-bold">€{(totalSpend / 100).toFixed(2)}</p>
        </div>
      </div>

      {/* Campaign List */}
      <div className="space-y-3">
        {campaigns.map((c) => (
          <div
            key={c.id}
            className="bg-white border border-gray-200 rounded-xl p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-900 truncate">{c.headline}</h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      c.status === 'ACTIVE'
                        ? 'bg-green-100 text-green-700'
                        : c.status === 'PAUSED'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {c.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1 truncate">{c.body_text}</p>
              </div>
              <div className="flex gap-2 shrink-0 ml-4">
                {c.status === 'ACTIVE' ? (
                  <button
                    onClick={() => pauseMutation.mutate(c.id)}
                    disabled={pauseMutation.isPending}
                    className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                  >
                    ⏸ Pause
                  </button>
                ) : c.status === 'PAUSED' ? (
                  <button
                    onClick={() => activateMutation.mutate(c.id)}
                    disabled={activateMutation.isPending}
                    className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    ▶ Aktivieren
                  </button>
                ) : null}
                <button
                  onClick={() => syncMutation.mutate(c.id)}
                  disabled={syncMutation.isPending}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                >
                  🔄 Sync
                </button>
              </div>
            </div>

            {/* Metrics row */}
            <div className="grid grid-cols-5 gap-4 mt-3 pt-3 border-t border-gray-100">
              <div>
                <p className="text-[10px] text-gray-400">Impressions</p>
                <p className="text-sm font-semibold">{(c.impressions ?? 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Clicks</p>
                <p className="text-sm font-semibold">{(c.clicks ?? 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">CTR</p>
                <p className="text-sm font-semibold">
                  {c.ctr != null ? `${c.ctr.toFixed(2)}%` : '–'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">CPC</p>
                <p className="text-sm font-semibold">
                  {c.cpc != null ? `€${c.cpc.toFixed(2)}` : '–'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Spend</p>
                <p className="text-sm font-semibold">€{((c.spend ?? 0) / 100).toFixed(2)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
